import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Talk } from '../types';

const TalkDetailPage: React.FC = () => {
  const { talkId } = useParams<{ talkId: string }>();
  const navigate = useNavigate();
  const [talk, setTalk] = useState<Talk | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (talkId) {
      fetchTalk();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talkId]);

  const fetchTalk = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/talks/${talkId}`);
      setTalk(response.data.data);
    } catch (err: any) {
      console.error('Error fetching talk:', err);
      setError(err.response?.data?.error?.message || 'Failed to load talk');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this talk?')) {
      return;
    }

    try {
      await apiClient.delete(`/v1/talks/${talkId}`);
      navigate('/talks');
    } catch (err: any) {
      console.error('Error deleting talk:', err);
      alert(err.response?.data?.error?.message || 'Failed to delete talk');
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !talk) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error || 'Talk not found'}
        </div>
        <Link to="/talks" className="btn btn-secondary">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Talks
        </Link>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="mb-3">
        <Link to="/talks" className="btn btn-link text-decoration-none">
          <i className="fas fa-arrow-left me-2"></i>
          Back to Talks
        </Link>
      </div>

      <div className="card">
        <div className="card-header bg-white">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h3 className="mb-1">{talk.title}</h3>
              <p className="text-muted mb-0">{talk.summary}</p>
            </div>
            <div className="d-flex gap-2">
              <Link
                to={`/talks/${talk.talk_id}/edit`}
                className="btn btn-outline-primary"
              >
                <i className="fas fa-edit me-1"></i>
                Edit
              </Link>
              <button
                onClick={handleDelete}
                className="btn btn-outline-danger"
              >
                <i className="fas fa-trash me-1"></i>
                Delete
              </button>
            </div>
          </div>
        </div>
        <div className="card-body">
          <h5 className="mb-3">Description</h5>
          <div className="mb-4" style={{ whiteSpace: 'pre-wrap' }}>
            {talk.description}
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <strong className="text-muted">Speaker:</strong>
                <div>{talk.speaker_name || 'Unknown'}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <strong className="text-muted">Created:</strong>
                <div>{new Date(talk.created_at).toLocaleString()}</div>
              </div>
            </div>
          </div>

          {talk.updated_at !== talk.created_at && (
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <strong className="text-muted">Last Updated:</strong>
                  <div>{new Date(talk.updated_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <h4>Sessions</h4>
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Session management will be available once you create sessions for this talk.
        </div>
      </div>
    </div>
  );
};

export default TalkDetailPage;
